/**
 * Shared TanStack Query client.
 *
 * Tuned for a field tablet on a poor link (the load-bearing reason React+TanStack
 * was chosen over Svelte, sec 7): generous staleTime, retry that backs off but
 * does NOT retry auth failures, and a global 401 handler that ends the session.
 */
import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api/client";

/** Session-expiry callback wired up by the app root (see main.tsx). */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/** Never retry a 4xx (client error / auth); do retry transient transport/5xx. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      // Session invalid — trigger the redirect once, don't retry.
      onUnauthorized?.();
      return false;
    }
    if (error.status >= 400 && error.status < 500) return false;
  }
  return failureCount < 3;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Field link is flaky: keep data usable, refetch conservatively.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: shouldRetry,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: shouldRetry,
    },
  },
});
